import { Label } from '@/components/ui/label';
import { SearchInput } from '@/components/ui/search-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import {
  AUDIT_PERIOD_OPTIONS,
  auditPeriodToStart,
  type AuditPeriod,
} from './_filter-options';
import { PeriodSelect } from './PeriodSelect';

// Re-exporta para compatibilidade com call sites (AuditoriaPage, OutboxPage,
// ChangeHistoryFilterBar etc.) que historicamente importavam destes nomes.
export { AUDIT_PERIOD_OPTIONS, auditPeriodToStart, type AuditPeriod };

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const ALVO_OPTIONS: { value: string; label: string }[] = [
  { value: 'auth', label: 'Autenticação' },
  { value: 'documento', label: 'Documento' },
  { value: 'artefato', label: 'Artefato' },
  { value: 'processo', label: 'Processo' },
  { value: 'certificado', label: 'Certificado' },
  { value: 'modelo', label: 'Modelo' },
  { value: 'user', label: 'Usuário' },
  { value: 'escritorio', label: 'Escritório' },
  { value: 'cliente', label: 'Cliente' },
  { value: 'comunicacao', label: 'Comunicação' },
  { value: 'cortesia_override', label: 'Cortesia' },
  { value: 'outbox_event', label: 'Outbox' },
];

const ACAO_OPTIONS: { value: string; label: string }[] = [
  { value: 'create', label: 'Criar' },
  { value: 'update', label: 'Atualizar' },
  { value: 'delete', label: 'Excluir' },
  { value: 'login', label: 'Login' },
  { value: 'login_failed', label: 'Login falhou' },
  { value: 'logout', label: 'Logout' },
  { value: 'upload', label: 'Upload' },
  { value: 'download', label: 'Download' },
  { value: 'toggle', label: 'Alternar' },
  { value: 'share', label: 'Compartilhar' },
  { value: 'sync', label: 'Sincronizar' },
  { value: 'mfa_success', label: 'MFA sucesso' },
  { value: 'mfa_failed', label: 'MFA falhou' },
  { value: 'password_changed', label: 'Senha alterada' },
];

const OUTCOME_OPTIONS: { value: string; label: string }[] = [
  { value: 'success', label: 'Sucesso' },
  { value: 'failure', label: 'Falha' },
];

const ACTOR_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'user', label: 'Usuário' },
  { value: 'admin', label: 'Admin' },
  { value: 'system', label: 'Sistema' },
  { value: 'webhook', label: 'Webhook' },
  { value: 'worker', label: 'Worker' },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

interface AuditoriaFilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  period: AuditPeriod;
  onPeriodChange: (value: AuditPeriod) => void;
  alvo: string;
  onAlvoChange: (value: string) => void;
  acao: string;
  onAcaoChange: (value: string) => void;
  outcome: string;
  onOutcomeChange: (value: string) => void;
  hideOutcome?: boolean;
  actorType: string;
  onActorTypeChange: (value: string) => void;
  hideAlvo?: boolean;
}

export function AuditoriaFilterBar({
  search,
  onSearchChange,
  period,
  onPeriodChange,
  alvo,
  onAlvoChange,
  acao,
  onAcaoChange,
  outcome,
  onOutcomeChange,
  hideOutcome,
  actorType,
  onActorTypeChange,
  hideAlvo,
}: AuditoriaFilterBarProps) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
      <div className="space-y-1">
        <Label className="text-xs font-medium">Buscar</Label>
        <SearchInput
          value={search}
          onChange={onSearchChange}
          placeholder="request_id, resource_id..."
          className="w-full sm:w-[200px]"
        />
      </div>

      {!hideAlvo && (
        <div className="space-y-1">
          <Label className="text-xs font-medium">Alvo</Label>
          <Select value={alvo} onValueChange={onAlvoChange}>
            <SelectTrigger className="w-full sm:w-[150px]">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              {ALVO_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-1">
        <Label className="text-xs font-medium">Ação</Label>
        <Select value={acao} onValueChange={onAcaoChange}>
          <SelectTrigger className="w-full sm:w-[140px]">
            <SelectValue placeholder="Todas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas</SelectItem>
            {ACAO_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!hideOutcome && (
        <div className="space-y-1">
          <Label className="text-xs font-medium">Resultado</Label>
          <Select value={outcome} onValueChange={onOutcomeChange}>
            <SelectTrigger className="w-full sm:w-[130px]">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              {OUTCOME_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-1">
        <Label className="text-xs font-medium">Tipo de ator</Label>
        <Select value={actorType} onValueChange={onActorTypeChange}>
          <SelectTrigger className="w-full sm:w-[130px]">
            <SelectValue placeholder="Todos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos</SelectItem>
            {ACTOR_TYPE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <PeriodSelect<AuditPeriod> value={period} onChange={onPeriodChange} />
    </div>
  );
}
